import { cva, type VariantProps } from 'class-variance-authority';
import {
  SPACE_PX,
  SPACE_PX_LG,
  SPACE_PX_MD,
  SPACE_STEPS,
  type SpaceStep,
} from '@/hen-ui/utils/space-steps';
import {
  SUBNAV_LABEL_STYLES,
  SUBNAV_LINK_HOVERS,
  SUBNAV_LINK_STYLES,
  SUBNAV_LINK_WEIGHT_CLASS,
  SUBNAV_MOBILE_BACK_COLORS,
  SUBNAV_MOBILE_HEADER_DIVIDERS,
  SUBNAV_MOBILE_HEADERS,
  SUBNAV_MOBILE_ITEM_SIZES,
  SUBNAV_MOBILE_LINK_WEIGHTS,
} from '@/hen-ui/composites/SubNav/subnav.variants';
import type {
  SubNavLabelStyle,
  SubNavLinkHover,
  SubNavLinkStyle,
  SubNavMobileBackColor,
  SubNavMobileHeader,
  SubNavMobileHeaderDivider,
  SubNavMobileItemSize,
  SubNavMobileLinkWeight,
} from '@/hen-ui/composites/SubNav/types';

export const NAVBAR_BOTTOM_EDGES = ['none', 'border', 'shadow'] as const;
export type NavbarBottomEdge = (typeof NAVBAR_BOTTOM_EDGES)[number];

/**
 * HEN Navbar variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 *
 * `bottomEdge` is applied after `variant`, so an explicit edge replaces the
 * border the `solid` style draws. Unset adds no class. `shadow` is the
 * small elevation token, the same one `panelShadow="sm"` uses.
 */
export const navbarVariants = cva(
  'relative w-full bg-white transition-all duration-normal',
  {
    variants: {
      variant: {
        default: '',
        transparent: 'bg-transparent',
        solid: 'bg-white border-b border-neutral-100',
      },
      sticky: {
        true: 'sticky top-0 z-nav',
        false: 'relative',
      },
      bottomEdge: {
        none: 'border-b-0',
        border: 'border-b border-neutral-100',
        shadow: 'border-b-0 shadow-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      sticky: true,
    },
  },
);

export type NavbarVariantProps = VariantProps<typeof navbarVariants>;

export const NAVBAR_LOGO_SIZES = ['sm', 'md', 'lg'] as const;
export type NavbarLogoSize = (typeof NAVBAR_LOGO_SIZES)[number];

/** Logo image height. Each size reads its own `--cds-navbar-logo-height*`. */
export const navbarLogoVariants = cva('w-auto max-w-full flex-shrink-0', {
  variants: {
    logoSize: {
      sm: 'h-navbar-logo-sm',
      md: 'h-nav-logo',
      lg: 'h-navbar-logo-lg',
    },
  },
  defaultVariants: {
    logoSize: 'md',
  },
});

/**
 * Width settings shared by the bar content and the drop-down panel. Each
 * named width is an existing layout token: `content` is the page content
 * width (`--width-content`) and `article` the article column width
 * (`--width-article-content`).
 */
export const NAVBAR_WIDTHS = ['full', 'content', 'article'] as const;
export type NavbarWidth = (typeof NAVBAR_WIDTHS)[number];

export const NAVBAR_CONTENT_WIDTHS = NAVBAR_WIDTHS;
export type NavbarContentWidth = NavbarWidth;

const NAVBAR_WIDTH_CAP = {
  full: '',
  content: 'mx-auto w-full max-w-content',
  article: 'mx-auto w-full max-w-article-content',
} as const satisfies Record<NavbarWidth, string>;

/**
 * The bar row. A named width caps the row at that token and centers it,
 * while the bar background and bottom edge still span the viewport.
 * Under a cap the Navbar puts the side padding (`barPaddingX`) on a
 * full-width wrapper, so the cap is the content width, as in the page
 * layouts.
 */
export const navbarBarRowVariants = cva(
  'relative flex h-nav items-center justify-between gap-2xl md:h-nav-lg',
  {
    variants: {
      contentWidth: NAVBAR_WIDTH_CAP,
    },
    defaultVariants: {
      contentWidth: 'full',
    },
  },
);

export const NAVBAR_PANEL_SHAPES = ['card', 'full-width'] as const;
export type NavbarPanelShape = (typeof NAVBAR_PANEL_SHAPES)[number];

export const NAVBAR_PANEL_WIDTHS = NAVBAR_WIDTHS;
export type NavbarPanelWidth = NavbarWidth;

/**
 * Wrapper around the desktop SubNav panel. `card` insets a rounded panel
 * from the viewport edges; `full-width` runs a square panel edge to edge,
 * flush under the bar. A card at a named width caps the wrapper's content
 * box at that token and adds the bar's side padding outside it, so the card
 * lines up with bar content of the same width and nothing transparent sits
 * beside it. A full-width panel keeps its edge-to-edge surface, which takes
 * the side padding, and caps its content instead
 * (`navbarPanelContentVariants`).
 */
export const navbarPanelVariants = cva('absolute left-0 right-0 z-50 hidden', {
  variants: {
    collapseBelow: {
      md: 'md:block',
      lg: 'lg:block',
    },
    panelShape: {
      card: 'px-sm pb-sm pt-xs',
      'full-width': '',
    },
    panelWidth: {
      full: '',
      content: '',
      article: '',
    },
  },
  compoundVariants: [
    { panelShape: 'card', collapseBelow: 'md', class: 'md:px-xl' },
    { panelShape: 'card', collapseBelow: 'lg', class: 'lg:px-xl' },
    {
      panelShape: 'card',
      panelWidth: 'content',
      class: 'mx-auto box-content max-w-content',
    },
    {
      panelShape: 'card',
      panelWidth: 'article',
      class: 'mx-auto box-content max-w-article-content',
    },
  ],
  defaultVariants: {
    panelShape: 'card',
    panelWidth: 'full',
    collapseBelow: 'lg',
  },
});

/**
 * Content cap inside a full-width panel, matching the bar row so the panel
 * columns line up with bar content of the same width. `full` adds nothing.
 */
export const navbarPanelContentVariants = cva('', {
  variants: {
    panelWidth: {
      full: '',
      content: NAVBAR_WIDTH_CAP.content,
      article: NAVBAR_WIDTH_CAP.article,
    },
  },
  defaultVariants: {
    panelWidth: 'full',
  },
});

export const NAVBAR_PANEL_SHADOWS = ['none', 'sm', 'lg'] as const;
export type NavbarPanelShadow = (typeof NAVBAR_PANEL_SHADOWS)[number];

/**
 * Classes for the visible panel surface (the SubNav itself), so the edge and
 * shadow follow the surface when a contained wrapper insets it.
 */
export const navbarPanelSurfaceVariants = cva('', {
  variants: {
    panelShape: {
      card: '',
      'full-width': 'border-t border-nav-panel-border',
    },
    panelShadow: {
      none: 'shadow-none',
      sm: 'shadow-sm',
      lg: 'shadow-lg',
    },
  },
  defaultVariants: {
    panelShape: 'card',
    panelShadow: 'none',
  },
});

export const NAVBAR_PANEL_OVERLAYS = ['dim', 'none'] as const;
export type NavbarPanelOverlay = (typeof NAVBAR_PANEL_OVERLAYS)[number];

export const NAVBAR_MENU_TRANSITIONS = ['none', 'fade'] as const;
export type NavbarMenuTransition = (typeof NAVBAR_MENU_TRANSITIONS)[number];

export const NAVBAR_MENU_COLUMNS = ['fill', 'fixed'] as const;
export type NavbarMenuColumns = (typeof NAVBAR_MENU_COLUMNS)[number];

/** Spacing settings take steps of the space scale (`--space-*`). */
export const NAVBAR_SPACE_STEPS = SPACE_STEPS;
export type NavbarSpace = SpaceStep;

/**
 * `barPaddingX` also takes `page`: the page side padding the page layouts
 * use (`--space-page-sides`, which grows with the screen), so capped bar
 * content lines up with capped page content at every width.
 */
export const NAVBAR_BAR_PADDINGS = [...SPACE_STEPS, 'page'] as const;
export type NavbarBarPadding = (typeof NAVBAR_BAR_PADDINGS)[number];

export const NAVBAR_BAR_PX: Readonly<Record<NavbarBarPadding, string>> = {
  ...SPACE_PX,
  page: 'px-page-sides',
};

/**
 * `collapseBelow` is the screen width where the bar switches from the menu
 * button and drawer to the desktop links and drop-down panels: `md` is
 * 768px, `lg` 1024px.
 */
export const NAVBAR_COLLAPSE_BREAKPOINTS = ['md', 'lg'] as const;
export type NavbarCollapseBelow = (typeof NAVBAR_COLLAPSE_BREAKPOINTS)[number];

interface NavbarCollapseClasses {
  /** Shown below the switch point only (the menu button). */
  mobileOnly: string;
  /** Hidden below the switch point, `flex` from it. */
  desktopFlex: string;
}

export const NAVBAR_COLLAPSE_CLASSES: Readonly<
  Record<NavbarCollapseBelow, NavbarCollapseClasses>
> = {
  md: { mobileOnly: 'md:hidden', desktopFlex: 'md:flex' },
  lg: { mobileOnly: 'lg:hidden', desktopFlex: 'lg:flex' },
};

const NAVBAR_BAR_PX_MD: Readonly<Record<NavbarBarPadding, string>> = {
  ...SPACE_PX_MD,
  page: 'md:px-page-sides',
};

const NAVBAR_BAR_PX_LG: Readonly<Record<NavbarBarPadding, string>> = {
  ...SPACE_PX_LG,
  page: 'lg:px-page-sides',
};

/** The bar's side padding from the switch point, where the panel shows. */
export const NAVBAR_BAR_PX_DESKTOP: Readonly<
  Record<NavbarCollapseBelow, Readonly<Record<NavbarBarPadding, string>>>
> = {
  md: NAVBAR_BAR_PX_MD,
  lg: NAVBAR_BAR_PX_LG,
};

/** Width of a left or right drawer, from the Drawer's own sizes. */
export const NAVBAR_DRAWER_SIZES = ['1/3', '1/2', '3/4', 'full'] as const;
export type NavbarDrawerSize = (typeof NAVBAR_DRAWER_SIZES)[number];

export const NAVBAR_DRAWER_HEADERS = SUBNAV_MOBILE_HEADERS;
export type NavbarDrawerHeader = SubNavMobileHeader;

export const NAVBAR_DRAWER_ITEM_SIZES = SUBNAV_MOBILE_ITEM_SIZES;
export type NavbarDrawerItemSize = SubNavMobileItemSize;

export const NAVBAR_DRAWER_HEADER_DIVIDERS = SUBNAV_MOBILE_HEADER_DIVIDERS;
export type NavbarDrawerHeaderDivider = SubNavMobileHeaderDivider;

export const NAVBAR_DRAWER_BACK_COLORS = SUBNAV_MOBILE_BACK_COLORS;
export type NavbarDrawerBackColor = SubNavMobileBackColor;

/** Weight of the desktop top-level links and the drawer rows. */
export const NAVBAR_LINK_WEIGHTS = SUBNAV_MOBILE_LINK_WEIGHTS;
export type NavbarLinkWeight = SubNavMobileLinkWeight;
export const NAVBAR_LINK_WEIGHT_CLASS = SUBNAV_LINK_WEIGHT_CLASS;

/**
 * `slide` moves the drawer over the page. `push` slides the elements marked
 * `data-drawer-push-target` aside with a left or right drawer, and slides
 * like `slide` when there are none.
 */
export const NAVBAR_DRAWER_TRANSITIONS = ['slide', 'push'] as const;
export type NavbarDrawerTransition = (typeof NAVBAR_DRAWER_TRANSITIONS)[number];

/**
 * `header` keeps the close button in the drawer header. `outside` moves it
 * onto the backdrop just past the drawer's open edge.
 */
export const NAVBAR_DRAWER_CLOSE_PLACEMENTS = ['header', 'outside'] as const;
export type NavbarDrawerClosePlacement =
  (typeof NAVBAR_DRAWER_CLOSE_PLACEMENTS)[number];

export const NAVBAR_MENU_LABEL_STYLES = SUBNAV_LABEL_STYLES;
export type NavbarMenuLabelStyle = SubNavLabelStyle;

export const NAVBAR_MENU_LINK_STYLES = SUBNAV_LINK_STYLES;
export type NavbarMenuLinkStyle = SubNavLinkStyle;

export const NAVBAR_MENU_LINK_HOVERS = SUBNAV_LINK_HOVERS;
export type NavbarMenuLinkHover = SubNavLinkHover;

export const NAVBAR_HOVER_STYLES = ['underline', 'highlight'] as const;
export type NavbarHoverStyle = (typeof NAVBAR_HOVER_STYLES)[number];

/**
 * Extra classes for desktop top-level links under `hoverStyle='highlight'`:
 * a tinted pill with an accent underline. Layered over the base link
 * classes, relying on tailwind-merge to drop the text underline.
 *
 * The pill is 40px tall with 24px side padding at every width. The space
 * steps change size across breakpoints, so the pill uses fixed sizes.
 *
 * The underline is a `::after` bar under the label (inset by the pill
 * padding) that grows from its center over `--cds-navbar-transition-duration`,
 * so it stays instant unless a theme sets that duration. The 2px bar sits flush
 * with the bottom edge of the pill.
 */
export const NAV_HIGHLIGHT_CLASS =
  "relative h-10 rounded-md px-6 hover:bg-nav-hover-bg hover:no-underline after:pointer-events-none after:absolute after:inset-x-6 after:bottom-0 after:h-[2px] after:origin-center after:scale-x-[0] after:bg-nav-hover-accent after:transition-transform after:duration-navbar after:content-[''] hover:after:scale-x-[1] motion-reduce:after:transition-none";

/**
 * Top-level links under `hoverStyle='highlight'` sit flush, so adjacent pills
 * touch and the labels are spaced by the pill padding alone (48px).
 */
export const NAV_HIGHLIGHT_GAP_CLASS = 'gap-0';

/** Keeps the highlight on while the item's drop-down menu is open. */
export const NAV_HIGHLIGHT_ACTIVE_CLASS = 'bg-nav-hover-bg after:scale-x-[1]';

/**
 * Hover timing for desktop top-level links. Reads
 * `--cds-navbar-transition-duration`; unset stays instant.
 */
export const NAV_LINK_TRANSITION_CLASS =
  'transition-colors duration-navbar motion-reduce:transition-none';
